import React, { useEffect, useRef, useState } from "react";
import { Filter, X, Check } from "lucide-react";

/**
 * MultiCheckFilter — Iter v34m (#3).
 * Unified multi-select filter popover for tables.
 *
 * Props:
 *   groups: [{ key: "status", label: "Status", options: [{value, label}] }, ...]
 *   value: { [groupKey]: string[] }  (selected values per group)
 *   onChange: (newValue) => void
 *   testidPrefix: string
 *
 * UX: button shows total selected count; popover lists groups with sectioned checkboxes.
 */
export default function MultiCheckFilter({ groups, value, onChange, testidPrefix = "mcf" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const totalSelected = Object.values(value || {}).reduce((s, arr) => s + (arr?.length || 0), 0);

  const toggle = (groupKey, optValue) => {
    const cur = value?.[groupKey] || [];
    const next = cur.includes(optValue) ? cur.filter((v) => v !== optValue) : [...cur, optValue];
    onChange({ ...value, [groupKey]: next });
  };

  const clearGroup = (groupKey) => onChange({ ...value, [groupKey]: [] });
  const clearAll = () => onChange(Object.fromEntries(groups.map((g) => [g.key, []])));

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1.5 px-3 py-2 border rounded-full text-xs uppercase tracking-wider transition-colors ${
          totalSelected > 0
            ? "bg-blue-50 border-blue-300 text-blue-700"
            : "bg-white border-stone-300 text-stone-600 hover:bg-stone-50 hover:border-stone-400"
        }`}
        data-testid={`${testidPrefix}-button`}
      >
        <Filter className="h-3.5 w-3.5" />
        <span>Filter</span>
        {totalSelected > 0 && (
          <span className="bg-blue-600 text-white px-1.5 py-0.5 text-[10px] rounded-full" data-testid={`${testidPrefix}-count`}>
            {totalSelected}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Iter v34ay — Mobile backdrop (tap outside untuk tutup bottom sheet) */}
          <div
            className="fixed inset-0 z-40 bg-black/40 sm:hidden"
            onClick={() => setOpen(false)}
            data-testid={`${testidPrefix}-backdrop`}
          />
          <div
            className="fixed sm:absolute left-0 right-0 bottom-0 sm:left-auto sm:right-0 sm:bottom-auto sm:top-full sm:mt-1 w-full sm:w-80 max-h-[85vh] sm:max-h-[480px] overflow-y-auto bg-white border border-stone-200 shadow-2xl rounded-t-3xl sm:rounded-2xl z-50"
            data-testid={`${testidPrefix}-popover`}
          >
            {/* Iter v34ay — Handle bar untuk bottom sheet mobile */}
            <div className="sm:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-stone-300"></div>
            </div>
            <div className="sticky top-0 bg-stone-50/95 backdrop-blur px-3 py-2 border-b border-stone-200 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-stone-700">
                {totalSelected > 0 ? `${totalSelected} dipilih` : "Belum ada filter"}
              </span>
              <div className="flex gap-2">
                {totalSelected > 0 && (
                  <button onClick={clearAll} className="text-[10px] uppercase tracking-wider text-red-600 hover:text-red-800" data-testid={`${testidPrefix}-clear-all`}>
                    Reset
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="text-stone-500 hover:text-stone-900" data-testid={`${testidPrefix}-close`}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
            {groups.map((group) => {
              const selected = value?.[group.key] || [];
              return (
                <div key={group.key} className="border-b border-stone-100 last:border-b-0">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-stone-50/50">
                    <span className="text-[11px] uppercase tracking-wider font-semibold text-stone-700">{group.label}</span>
                    {selected.length > 0 && (
                      <button onClick={() => clearGroup(group.key)} className="text-[10px] text-stone-500 hover:text-red-700" data-testid={`${testidPrefix}-clear-${group.key}`}>
                        Bersihkan ({selected.length})
                      </button>
                    )}
                  </div>
                  <div className="p-2 max-h-44 overflow-y-auto">
                    {(!group.options || group.options.length === 0) && (
                      <p className="text-xs text-stone-400 italic px-2 py-1">(Tidak ada opsi)</p>
                    )}
                    {group.options.map((opt) => {
                      const checked = selected.includes(opt.value);
                      return (
                        <label key={opt.value} className="flex items-center gap-2 text-xs px-2 py-2 sm:py-1.5 hover:bg-stone-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(group.key, opt.value)}
                            data-testid={`${testidPrefix}-${group.key}-${opt.value}`}
                          />
                          <span className={checked ? "text-stone-900 font-medium" : "text-stone-700"}>{opt.label}</span>
                          {checked && <Check className="h-3 w-3 text-emerald-600 ml-auto" />}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {/* Iter v34ay — Sticky bottom button on mobile untuk terapkan */}
            <div className="sm:hidden sticky bottom-0 bg-white border-t border-stone-200 p-3">
              <button
                onClick={() => setOpen(false)}
                className="w-full py-3 rounded-full bg-stone-900 text-white text-sm font-medium hover:bg-blue-700 transition"
                data-testid={`${testidPrefix}-apply`}
              >
                Terapkan Filter{totalSelected > 0 ? ` (${totalSelected})` : ""}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
